import { useEffect, useRef, useState } from 'react';
import { ReactComponent as Arrow } from '../../assets/icons/trvto_arrowRightWhite.svg';
import './accommodationTabBar.css';

/*
 * Single source of truth for the accommodation details tab list - both
 * the support/org read-only view (SupOrgAccommodationDetails.jsx) and the
 * customer-facing camp details page (CampDetails.jsx) render the same
 * accommodation record and should show the same tabs, in the same order,
 * with the same labels. Previously each page kept its own copy of this
 * array (with slightly different labels/ordering) plus its own tab-bar
 * markup and CSS - this is that logic pulled into one place so both
 * sides stay in sync going forward.
 */
export const ACCOMMODATION_TABS = [
    { id: 'basic', label: 'Overview' },
    { id: 'units', label: 'Rooms & Units' },
    { id: 'amenities', label: 'Amenities' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'services', label: 'Services' },
    { id: 'policies', label: 'Policies' },
    { id: 'media', label: 'Photos & Videos' },
    { id: 'location', label: 'Location' },
    { id: 'additional', label: 'Additional Info' }
];

/*
 * Renders the scrollable pill tab bar + the "more tabs" scroll arrow
 * (ported as-is from SupOrgAccommodationDetails.jsx's inline
 * tabsRef/showTabArrow/scrollTabs logic - the arrow only shows up when
 * the tab row is actually overflowing and not already scrolled to the
 * end). Which tab is active and what renders below it stays owned by
 * the caller - this component only renders the tab row itself and
 * reports clicks back via onChange, so each page can keep feeding its
 * own content (ReviewSubmit on the customer side, the Section
 * components on the support side) to whichever tab is active.
 *
 * `sticky` opts into the support side's sticky-under-the-header
 * behavior (position: sticky, top offset handled entirely in
 * accommodationTabBar.css so its mobile breakpoint override still
 * applies); left off by default so a page that doesn't have that
 * fixed header can use the same component without it jumping around.
 */
export default function AccommodationTabBar({ tabs = ACCOMMODATION_TABS, activeTab, onChange, sticky = false }) {
    const tabsRef = useRef(null);
    const [showTabArrow, setShowTabArrow] = useState(false);

    useEffect(() => {
        const element = tabsRef.current;

        if (!element) return;

        const checkTabScroll = () => {
            const isScrollable = element.scrollWidth > element.clientWidth;

            const isAtEnd = element.scrollLeft + element.clientWidth >= element.scrollWidth - 5;

            setShowTabArrow(isScrollable && !isAtEnd);
        };

        checkTabScroll();

        element.addEventListener('scroll', checkTabScroll);

        window.addEventListener('resize', checkTabScroll);

        return () => {
            element.removeEventListener('scroll', checkTabScroll);

            window.removeEventListener('resize', checkTabScroll);
        };
    }, [activeTab, tabs]);

    const scrollTabs = () => {
        if (!tabsRef.current) return;

        tabsRef.current.scrollBy({
            left: 220,
            behavior: 'smooth'
        });
    };

    return (
        <div className={`accTabBar-container ${sticky ? 'accTabBar-sticky' : ''}`}>
            <div className='accTabBar-tabs' ref={tabsRef}>
                {tabs.map((tab) => {
                    const isActive = activeTab === tab.id;

                    return (
                        <button
                            type='button'
                            key={tab.id}
                            className={`accTabBar-tabButton ${isActive ? 'accTabBar-tabButtonActive' : ''}`}
                            onClick={() => onChange(tab.id)}
                        >
                            {tab.label}
                        </button>
                    );
                })}
            </div>

            {showTabArrow && (
                <button type='button' className='accTabBar-tabArrow' onClick={scrollTabs}>
                    <Arrow />
                </button>
            )}
        </div>
    );
}
